import type { SpotlightPosition } from '../model/types';

interface SpotlightLayerProps {
  position: SpotlightPosition | null;
  targetElement: Element | null;
  label: string;
  onTargetHover?: () => void;
  /**
   * Forward a tap on the highlight to the real element. Off for tutorial tours: a tap there would
   * open the real invitation dialog or generate a real link under the backdrop.
   */
  relayClick?: boolean;
}

/** Dimmed backdrop with a hole and a clickable ring on the highlighted element. */
export const SpotlightLayer = ({
  position,
  targetElement,
  label,
  onTargetHover,
  relayClick = true,
}: SpotlightLayerProps) => (
  <>
    <svg className="fixed inset-0 w-full h-full pointer-events-none" aria-hidden="true">
      <defs>
        <mask id="spotlight-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          {position && (
            <rect
              x={position.left}
              y={position.top}
              width={position.width}
              height={position.height}
              rx="12"
              fill="black"
            />
          )}
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width="100%"
        height="100%"
        fill="rgba(15, 23, 42, 0.75)"
        mask="url(#spotlight-mask)"
        className="pointer-events-auto"
      />
    </svg>

    {position && (
      <>
        <div
          className="fixed rounded-xl ring-4 ring-brand-400 ring-opacity-60 animate-pulse motion-reduce:animate-none pointer-events-none"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            height: position.height,
            boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.3), 0 0 40px rgba(255, 255, 255, 0.25)',
          }}
          aria-hidden="true"
        />
        {relayClick && (
          <button
            type="button"
            className="fixed rounded-xl cursor-pointer pointer-events-auto z-[10001] focus-visible:ring-4 focus-visible:ring-brand-400"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              height: position.height,
              background: 'transparent',
            }}
            onMouseEnter={onTargetHover}
            onClick={() => {
              if (targetElement instanceof HTMLElement) targetElement.click();
            }}
            aria-label={label}
          />
        )}
      </>
    )}
  </>
);
